<template>
  <div class="container-fluid">
    <IntroTitle
      :title="experienceTypeName"
      :subtitle="experienceTypeDescription"
      :subtitleMobile="false"
      padding="1dvw 30px"
      paddingDesktop="3dvw 50px 0px"
      titleFontSize="1.6rem"
      titleMobileFontSize="1.3rem"
      descriptionAlign="initial"
      descriptionMobileAlign="center"
      transform="uppercase"
      page="ExperienceTypes"
      effect="top-to-bottom--effect"
    >
    </IntroTitle>
    <div class="experiences">
      <Tabs
        v-if="hayActivas && futureExperiences && futureExperiences.length > 0"
        @changeExperiences="changeExperiences"
      >
        <Tab title="experiences-by-type.tabs.tab-0.title">
          <div class="cards-experiences">
            <template v-if="isLoading">
              <CardSkeleton
                imageHeight="160px"
                v-for="index in 4"
                :key="index"
              />
            </template>
            <template v-else>
              <Card
                v-for="(experience, index) in displayedExperiences"
                :image="imageHost + experience.reducedImageKey"
                :completo="experience.soldOut === true"
                :info="true"
                :location="
                  experience.attributes.find(
                    (attribute) =>
                      attribute.experienceAttributeTypeCode === 'LOCATION'
                  )?.value
                "
                :title="translate(experience.title)"
                :subtitle="truncate(translate(experience.summary) || '', 80)"
                imageWidth="40%"
                imageDesktopWidth="40%"
                descriptionWidth="70%"
                descriptionDesktopWidth="100%"
                imageHeight="100%"
                titleSize="16px"
                subtitleSize="12px"
                labelGap="5px"
                descriptionJustify="space-between"
                @click="goTo(experience.code)"
                :key="index"
              />
            </template>
          </div>
        </Tab>
        <Tab title="experiences-by-type.tabs.tab-1.title">
          <div class="cards-experiences">
            <template v-if="isLoading">
              <CardSkeleton
                imageHeight="160px"
                v-for="index in 4"
                :key="index"
              />
            </template>
            <template v-else>
              <Card
                v-for="(experience, index) in displayedExperiences"
                :image="imageHost + experience.reducedImageKey"
                :completo="experience.soldOut === true"
                :info="false"
                :title="translate(experience.title)"
                :subtitle="truncate(translate(experience.summary) || '', 80)"
                imageWidth="40%"
                imageDesktopWidth="40%"
                descriptionWidth="70%"
                descriptionDesktopWidth="100%"
                imageHeight="100%"
                titleSize="16px"
                subtitleSize="12px"
                labelGap="5px"
                descriptionJustify="space-between"
                @click="goTo(experience.code)"
                :key="index"
              />
            </template>
          </div>
        </Tab>
      </Tabs>
      <div class="cards-experiences" v-else>
        <template v-if="isLoading">
          <CardSkeleton
            imageHeight="160px"
            height="40dvh"
            v-for="index in 4"
            :key="index"
          />
        </template>
        <template v-else>
          <Card
            v-for="(experience, index) in displayedExperiences"
            :image="imageHost + experience.reducedImageKey"
            :completo="experience.soldOut === true"
            :info="true"
            :location="
              experience.attributes.find(
                (attribute) =>
                  attribute.experienceAttributeTypeCode === 'LOCATION'
              )?.value
            "
            :title="translate(experience.title)"
            :subtitle="truncate(translate(experience.summary) || '', 80)"
            imageWidth="40%"
            imageDesktopWidth="40%"
            descriptionWidth="70%"
            descriptionDesktopWidth="100%"
            imageHeight="100%"
            titleSize="16px"
            subtitleSize="12px"
            labelGap="5px"
            descriptionJustify="space-between"
            @click="goTo(experience.code)"
            :key="index"
          />
        </template>
      </div>
      <!-- Estado vacío: sin experiencias activas ni futuras (p.ej. actividad caducada) -->
      <div class="experiences-empty" v-if="showFinished">
        <IconCalendarOff :size="40" color="#008EDD" />
        <p class="experiences-empty__text">
          {{ $t('experiences-by-type.finished') }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
  import CardSkeleton from '@/components/movistar/skeletons/CardSkeleton.vue'
  import Card from '@/components/movistar/cards/Card.vue'
  import Tabs from '@/components/movistar/common/Tabs.vue'
  import Tab from '@/components/movistar/common/Tab.vue'
  import IntroTitle from '@/components/movistar/common/IntroTitle.vue'
  import { IconCalendarOff } from '@tabler/icons-vue'
  import router from '@/router'
  import { computed, ref, onMounted, watch } from 'vue'
  import { useStore } from 'vuex'
  import commonMethods from '@/commonMethods'

  const { registerError, translate, truncate, scrollToClassName } = commonMethods()
  const store = useStore()

  const imageHost = ref(process.env.VUE_APP_IMAGE_HOST)
  const isLoading = ref(true)
  const experiences = computed(() => store.getters['getExperiences'])
  const types = computed(() => store.getters['getExperienceTypes'])
  const props = defineProps({
    experienceTypeCode: { type: String }
  })

  let experienceTypeName = ref('')
  let experienceTypeDescription = ref('')

  let activeExperiences = experiences.value.filter((experience) => {
    let now = new Date()
    let from = new Date(experience.startDate)
    from.setDate(from.getDate())

    let to = new Date(experience.endDate)

    return (
      (now >= from && experience.endDate == null) || (now >= from && now <= to)
    )
  })
  const futureExperiences = ref(
    experiences.value.filter((experience) => {
      let now = new Date()
      let from = new Date(experience.startDate)
      from.setDate(from.getDate())
      return now < from
    })
  )

  const hayActivas = ref(activeExperiences.length > 0)
  const displayedExperiences = ref(activeExperiences.length > 0 ? activeExperiences : futureExperiences.value)

  // No hay nada que enseñar: ni activas ni futuras. Es el caso de la actividad
  // caducada (antes se quedaba en blanco bajo el título).
  const showFinished = computed(
    () =>
      !isLoading.value &&
      displayedExperiences.value.length === 0 &&
      futureExperiences.value.length === 0
  )

  watch(experiences, (newValue) => {
    if (newValue) {
      futureExperiences.value = newValue.filter((experience) => {
        let now = new Date()
        let from = new Date(experience.startDate)
        from.setDate(from.getDate())
        return now < from
      })

      activeExperiences = newValue.filter((experience) => {
        let now = new Date()
        let from = new Date(experience.startDate)
        from.setDate(from.getDate())

        let to = new Date(experience.endDate)

        return (
          (now >= from && experience.endDate == null) ||
          (now >= from && now <= to)
        )
      })

      hayActivas.value = activeExperiences.length > 0
      displayedExperiences.value = hayActivas.value ? activeExperiences : futureExperiences.value
    }
  })

  const changeExperiences = (event) => {
    if (event.index === 0) {
      displayedExperiences.value = activeExperiences
    } else if (event.index === 1) {
      displayedExperiences.value = futureExperiences.value
    }
  }

  const goTo = async (experienceCode) => {
    router.push({
      name: 'Experience',
      params: { experienceCode: experienceCode }
    })
  }

  onMounted(async () => {
    try {
      const scrollToClass = router.currentRoute.value.query.scrollToClass
      if (scrollToClass) {
        scrollToClassName(scrollToClass)
      }
      await store.dispatch('getExperiencesByTypeCode', props.experienceTypeCode)
      while (!types.value || types.value.length == 0) {
        await new Promise((r) => setTimeout(r, 200))
      }

      const type = types.value.find(
        (ftype) => ftype.code == props.experienceTypeCode.toUpperCase()
      )

      experienceTypeName.value = translate(type.title)
      experienceTypeDescription.value = translate(type.description)
    } catch (error) {
      registerError(error, 'getExperiencesByTypeCode')
    } finally {
      isLoading.value = false
    }
  })
</script>

<style lang="scss">
  @import '@/scss/mixings';

  .experiences-empty {
    @include d-flex(column, center, center, 12px);
    padding: 60px 30px 80px;
    text-align: center;
    &__text {
      color: #313235;
      font-size: 1.1rem;
      font-weight: 500;
    }
  }

  .cards-experiences {
    @include d-flex(column, space-between, initial, 20px);
    padding: 20px;

    @media screen and (min-width: $sm-breakpoint) {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 20px;
      padding: 20px 50px;
    }

    @media screen and (min-width: $md-breakpoint) {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 50px;
      padding: 15px 50px 50px 50px;
    }

    @media screen and (min-width: $lg-breakpoint) and (orientation: landscape) {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 50px;
      padding: 15px 50px 50px 50px;
    }
  }
</style>
